import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { handbookSections } from "../data/mock";
import { BookOpen, Search, Download, FileText } from "lucide-react";

const Handbook = () => {
  const [query, setQuery] = useState("");

  const filtered = handbookSections.filter((s) => {
    const q = query.toLowerCase();
    if (!q) return true;
    return (
      s.title.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      s.items.some((it) => it.toLowerCase().includes(q))
    );
  });

  return (
    <PageLayout>
      <PageHero
        badge="Reference Library"
        title="Handbook & Reference"
        subtitle="Quick references, charts, and standards used by our team — plain-English explanations for the most common wire & cable questions."
        breadcrumbs={[{ label: "Handbook & Reference" }]}
      />

      {/* Search */}
      <section className="py-12 bg-white">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search handbook (e.g. ampacity, mil-spec, fiber)…"
              className="w-full pl-12 pr-5 py-4 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 bg-white shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="pb-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6 pt-12">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              No matches found. Try a different keyword.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filtered.map((s, idx) => (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-3xl p-8 bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md shadow-emerald-500/20"
                      style={{
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      }}
                    >
                      <BookOpen className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-1" style={{ color: "#0a2e1f" }}>
                        {s.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {s.description}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2 pl-1">
                    {s.items.map((it, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-slate-700"
                      >
                        <FileText size={14} className="text-emerald-600 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Downloads */}
      <section className="py-20 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <div
            className="rounded-3xl p-10 md:p-14 grid md:grid-cols-2 items-center gap-8"
            style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" }}
          >
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-3 px-3 py-1 rounded-full bg-white text-emerald-700">
                Free Resource
              </span>
              <h3 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight" style={{ color: "#0a2e1f" }}>
                Download our Cable Spec Cheat Sheet
              </h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                A 4-page printable PDF covering AWG charts, mil-spec quick refs,
                heat-shrink ratios and fiber connector types — perfect for your
                truck binder.
              </p>
              <button
                onClick={() => alert("Download will be available soon!")}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.03] transition-all"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
              >
                <Download size={16} /> Download PDF
              </button>
            </div>
            <div className="hidden md:flex justify-center">
              <div
                className="relative w-56 h-72 rounded-2xl bg-white shadow-2xl rotate-[-6deg] flex items-center justify-center border border-emerald-100"
              >
                <FileText size={64} className="text-emerald-500" />
                <div className="absolute -bottom-3 -right-3 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg" style={{ background: "linear-gradient(135deg, #10b981, #047857)" }}>
                  PDF
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default Handbook;
